import { withStyles } from '@material-ui/core';
import { useEffect, useRef, useState } from 'react';
import { withAlert } from 'react-alert';
import { Redirect, Route, Switch } from 'react-router';
import { HashRouter } from 'react-router-dom';
import FavoriteReportEdit from './components/FavoriteReports/FavoriteReportEdit/FavoriteReportEdit';
import FwApns from './components/Fw/FwApns';
import FwConfig from './components/Fw/FwConfig';
import FwFiles from './components/Fw/FwFiles';
import FwLogs from './components/Fw/FwLogs';
import FwReleaseAudit from './components/Fw/FwReleaseAudit';
import FwSignActivity from './components/Fw/FwSignActivity';
import FwStalledPairs from './components/Fw/FwStalledPairs';
import FwTaskDetail from './components/Fw/FwTaskDetail';
import FwTaskForm from './components/Fw/FwTaskForm';
import FwTasks from './components/Fw/FwTasks';
import ListGenerator from './components/GeneratedList/ListGenerator';
import TestListGenerator from './components/GeneratedList/TestListGenerator';
import TestListGeneratorV2 from './components/GeneratedList/TestListGeneratorV2';
import Generator from './components/Generator/Generator';
import ListTemplates from './components/ListTemplates/ListTemplates';
import LoginPage from './components/Login/LoginPage';
import PrivateRoute from './components/Login/PrivateRoute';
import Docs from './components/Nav/Docs';
import Error from './components/Nav/Error';
import GatewaysList from './components/Nav/GatewaysList';
import Home from './components/Nav/Home';
import ListForms from './components/Nav/Lists/ListForms';
import Packages from './components/Nav/Packages';
import Pipelines from './components/Nav/Pipelines';
import PipelinesList from './components/Nav/PipelinesList';
import ProtocolsList from './components/Nav/ProtocolsList';
import Settings from './components/Nav/Settings';
import Sms from './components/Nav/Sms';
import UsersList from './components/Nav/UsersList';
import PackagePreview from './components/PackagePreview/PackagePreview';
import SerialNumbers from './components/SerialNumbers/SerialNumbers';
import TestListOrdered from './components/TestListOrdered/TestListOrdered';
import { AppProvider } from './context/AppContext';
import { axiosInstance, registerSessionExpiredHandler } from './utils/axiosInstance';
import { endpoint, localStorageKey } from './utils/constants';
import { clearSessionProfile, saveSessionProfile } from './utils/helpers';
import Snow from './utils/Snow';

const styles = {
    pathLink: {
        marginBottom: '0.625rem',
    },
};

const App = ({ classes, alert: { show } }) => {
    const [submenuElement, setSubmenuElement] = useState(null);
    const [sessionStatus, setSessionStatus] = useState('checking');
    const sessionIdentity = useRef(null);
    const isAuthenticated = sessionStatus === 'authenticated';

    useEffect(() => {
        const endSession = () => {
            sessionIdentity.current = null;
            setSessionStatus('anonymous');
        };
        registerSessionExpiredHandler(endSession);

        axiosInstance
            .get(endpoint.USER_CURRENT)
            .then(response => {
                const profile = response.data;

                if (profile?.role === undefined) {
                    clearSessionProfile();
                    setSessionStatus('anonymous');
                    return;
                }

                sessionIdentity.current = saveSessionProfile({
                    id: profile.id,
                    role: profile.role,
                    permissions: profile.permissions,
                    isExternal: profile.is_external,
                });
                setSessionStatus('authenticated');
            })
            .catch(() => {
                clearSessionProfile();
                setSessionStatus('anonymous');
            });

        const handleIdentityChange = event => {
            if (
                event.key !== localStorageKey.USER_ID ||
                !event.newValue ||
                event.newValue === event.oldValue
            ) {
                return;
            }

            if (
                sessionIdentity.current === null ||
                String(sessionIdentity.current) === String(event.newValue)
            ) {
                return;
            }

            endSession();
        };

        window.addEventListener('storage', handleIdentityChange);

        return () => {
            registerSessionExpiredHandler(null);
            window.removeEventListener('storage', handleIdentityChange);
        };
    }, []);

    const handleLogin = (login, password, isExternal) => {
        axiosInstance
            .post(
                endpoint.USER_LOGIN,
                {
                    login,
                    password,
                    is_external: isExternal,
                },
                {
                    headers: {
                        Accept: 'application/json, text/plain, */*',
                        'Content-Type': 'application/json',
                    },
                }
            )
            .then(response => {
                if (response.data.status === 'success') {
                    sessionIdentity.current = saveSessionProfile({
                        id: response.data.id,
                        role: response.data.role,
                        permissions: response.data.permissions,
                        isExternal: response.data.is_external ?? isExternal,
                    });
                    setSessionStatus('authenticated');
                }
            })
            .catch(() => show('NIEPRAWIDŁOWE DANE '));
    };

    const isSnowing = () => {
        const getEnvValue = process.env.REACT_APP_IS_SNOWING;
        const isWinter = () => {
            const getMonth = new Date().getMonth();
            return getMonth === 11 || getMonth === 0;
        };

        return getEnvValue === 'true' && isWinter();
    };

    const handleListsSubmenu = element => {
        setSubmenuElement(element);
    };

    return (
        <HashRouter>
            <AppProvider
                value={{
                    handleListsSubmenu,
                    setSubmenuElement,
                    submenuElement,
                    isAuthenticated,
                    endSession: () => {
                        sessionIdentity.current = null;
                        setSessionStatus('anonymous');
                    },
                }}>
                <div>
                    {isSnowing() && <Snow />}

                    {sessionStatus === 'checking' ? null : (
                        <Switch>
                            <Route
                                handleLogin={handleLogin}
                                className={classes.pathLink}
                                exact
                                path="/login"
                                render={props => (
                                    <LoginPage
                                        {...props}
                                        handleLogin={handleLogin}
                                        isAuthenticated={isAuthenticated}
                                    />
                                )}
                            />
                            <PrivateRoute
                                permission="waterworks"
                                exact
                                strict
                                className={classes.pathLink}
                                path="/pipelines"
                                component={props => <PipelinesList {...props} showWaterpipeLink />}
                            />
                            <PrivateRoute
                                permission="waterworks"
                                exact
                                strict
                                className={classes.pathLink}
                                path="/pipelines/add"
                                component={Pipelines}
                            />
                            <PrivateRoute
                                permission="waterworks"
                                exact
                                strict
                                className={classes.pathLink}
                                path="/pipeline/edit/:id"
                                component={Pipelines}
                            />
                            <PrivateRoute
                                strict
                                className={classes.pathLink}
                                path="/pipeline/:id"
                                component={Docs}
                            />
                            <PrivateRoute
                                permission="packages"
                                className={classes.pathLink}
                                path="/packages"
                                component={Packages}
                            />
                            <PrivateRoute
                                permission="packages"
                                className={classes.pathLink}
                                path="/error/:id"
                                component={Error}
                            />
                            <PrivateRoute
                                permission="packages"
                                className={classes.pathLink}
                                path="/package-preview/:id"
                                component={props => <PackagePreview key={props.match.params.id} {...props} />}
                            />
                            <PrivateRoute
                                permission="packages"
                                className={classes.pathLink}
                                path="/docs"
                                component={Docs}
                            />
                            <PrivateRoute
                                permission="generator"
                                className={classes.pathLink}
                                path="/generator"
                                component={Generator}
                            />
                            <PrivateRoute
                                permission="generator"
                                className={classes.pathLink}
                                path="/sms"
                                component={Sms}
                            />
                            <PrivateRoute
                                permission="gateways"
                                className={classes.pathLink}
                                path="/gateways"
                                component={GatewaysList}
                            />
                            <PrivateRoute
                                permission="users"
                                className={classes.pathLink}
                                path="/users"
                                component={UsersList}
                            />
                            <PrivateRoute
                                permission="protocols"
                                className={classes.pathLink}
                                path="/protocols"
                                component={ProtocolsList}
                            />
                            <PrivateRoute
                                className={classes.pathLink}
                                path="/settings"
                                component={Settings}
                            />
                            <PrivateRoute
                                permission="summaries_production"
                                exact
                                className={classes.pathLink}
                                path="/lists/1"
                                component={ListForms}
                            />
                            <PrivateRoute
                                permission="summaries_test"
                                exact
                                className={classes.pathLink}
                                path="/lists/2"
                                component={ListForms}
                            />
                            <PrivateRoute
                                permission="summaries_production"
                                className={classes.pathLink}
                                path="/generated-list"
                                component={ListGenerator}
                            />
                            <PrivateRoute
                                permission="summaries_test"
                                className={classes.pathLink}
                                path="/test-generated-list"
                                component={TestListGenerator}
                            />
                            <PrivateRoute
                                permission="summaries_test"
                                className={classes.pathLink}
                                path="/test-generated-list-v2"
                                component={TestListGeneratorV2}
                            />
                            <PrivateRoute
                                permission="summaries_test"
                                className={classes.pathLink}
                                path="/test-ordered-list"
                                component={TestListOrdered}
                            />
                            <PrivateRoute
                                permission="summaries_production"
                                exact
                                className={classes.pathLink}
                                path="/list-templates/1"
                                component={ListTemplates}
                            />
                            <PrivateRoute
                                permission="summaries_test"
                                exact
                                className={classes.pathLink}
                                path="/list-templates/2"
                                component={ListTemplates}
                            />
                            <PrivateRoute
                                permission={['summaries_production', 'summaries_test']}
                                className={classes.pathLink}
                                path="/list-template/edit/:id"
                                component={FavoriteReportEdit}
                            />
                            <PrivateRoute
                                permission="uniques"
                                className={classes.pathLink}
                                path="/serial-numbers"
                                component={SerialNumbers}
                            />
                            <PrivateRoute
                                permission="fw"
                                className={classes.pathLink}
                                path="/fw-tasks"
                                component={FwTasks}
                            />
                            <PrivateRoute
                                permission="fw"
                                className={classes.pathLink}
                                path="/fw-apns"
                                component={FwApns}
                            />
                            <PrivateRoute
                                permission="fw"
                                className={classes.pathLink}
                                path="/fw-files"
                                component={FwFiles}
                            />
                            <PrivateRoute
                                permission="fw"
                                className={classes.pathLink}
                                path="/fw-logs"
                                component={FwLogs}
                            />
                            <PrivateRoute
                                permission="fw"
                                className={classes.pathLink}
                                path="/fw-task/create"
                                component={FwTaskForm}
                            />
                            <PrivateRoute
                                permission="fw"
                                className={classes.pathLink}
                                path="/fw-task/:id"
                                component={FwTaskDetail}
                            />
                            <PrivateRoute
                                permission="fw"
                                className={classes.pathLink}
                                path="/fw-stalled-pairs"
                                component={FwStalledPairs}
                            />
                            <PrivateRoute
                                permission="fw"
                                className={classes.pathLink}
                                path="/fw-release-audit"
                                component={FwReleaseAudit}
                            />
                            <PrivateRoute
                                permission="fw"
                                className={classes.pathLink}
                                path="/fw-sign-activity"
                                component={FwSignActivity}
                            />
                            <PrivateRoute
                                permission="fw"
                                className={classes.pathLink}
                                path="/fw-config"
                                component={FwConfig}
                            />
                            <PrivateRoute
                                permission="start"
                                className={classes.pathLink}
                                exact
                                strict
                                path="/"
                                component={Home}
                            />
                            <Redirect from="*" to="/" />
                        </Switch>
                    )}
                </div>
            </AppProvider>
        </HashRouter>
    );
};

export default withStyles(styles)(withAlert()(App));
